import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Tooltip } from '@mui/material';
import amplitudeLog from '@client/amplitude';
import {
  AMP_BUILDING_VERSION_ADD,
  AMP_BUILDING_VERSION_DELETE,
  AMP_BUILDING_VERSION_SELECT,
  AMP_BUILDING_VERSION_UPDATE_NAME,
} from '@shared/constants';
import {
  useResolvedProjectTarget,
  useScopeTargetLabel,
} from '@features/scope/scope-targets.hooks';
import { Icon } from '@ui/Icon';
import { requestScopeOverlay } from '@features/scope/scope-overlay.store';
import { useSelectedVersion, useUIState } from '@client/store/ui';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useVersions } from '@client/hooks/use-elements.hooks';
import {
  useProjectNavigation,
  useRawProjectParams,
} from '@client/hooks/use-router.hooks';
import {
  useAddVersion,
  useApplyProjectChanges,
  getRemoveVersionConfirmOptions,
  useProject,
  useProjectId,
  useRemoveVersion,
  useUpdateProject,
} from '@client/store/project';
import { getVersionDeleteNavigation } from '@client/store/project/apply-version-navigation.helpers';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { IconButton } from '@ui/buttons';
import { PillTabs } from '@ui/PillTabs';
import { Menu } from '@ui/menus/Menu';
import {
  buildProposalEffectiveColorIndexMap,
  getProposalSwatchColor,
} from '@shared/helpers/proposal.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { applyVersionMetadata } from '@shared/helpers/project.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getCo2eChartDataFromResults } from '@ui/BarChart/helpers/bar-chart.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { getProposals } from '@shared/helpers/proposals';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type { Baseline } from '@shared/models/project.model';
import type { IProposal, ProposalId } from '@shared/models/proposals.model';
import type { Results, ResultsRecord } from '@shared/models/unit.model';
import {
  computeProposalOverviewCo2PerGfaScaleMax,
  computeProposalOverviewCostPerGfaScaleMax,
} from '@shared/helpers/proposals/proposals-overview-scale.helpers';
import {
  useGetResultsPerGFA,
  useResultsPerGFA,
} from '@features/results/use-results.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import {
  BASELINE_PROPOSAL_SEARCH_VALUE,
  PROPOSAL_SEARCH_PARAM,
  getSelectedProposalSearchValueFromUrl,
  proposalIdFromSearchValue,
  useSelectedProposalId,
  useSetSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import {
  getProposalToRememberAfterLeavingVersion,
  getRememberedProposalForVersion,
  rememberProposalForVersion,
} from '@client/hooks/use-router-query-params.hooks';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import {
  useProposalsBarChartValues,
  useProposalsResultRecords,
} from './use-proposals.hooks';
import {
  useAddProposal,
  useDeleteProposalShortcut,
  useProposals,
  useReorderProposals,
} from './hooks/use-proposals.hooks';
import BaselineBar from './ProposalsOverview/BaselineBar';
import ProposalBar from './ProposalsOverview/ProposalBar';
import { OverviewBarShell } from '@features/results/OverviewBarShell';
import {
  hasVisibleProposalTarget,
  PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR,
  PROPOSALS_OVERVIEW_DIFF_CHIP,
} from './proposals-overview-bar-layout.helpers';
import { useSharedCo2LabelIconVisibility } from './use-shared-co2-label-icon-visibility.hook';
import {
  OPTIMISTIC_VERSION_ADD_CANCELLED_EVENT,
  OPTIMISTIC_VERSION_ADDED_EVENT,
  type OptimisticVersionAddedEventDetail,
} from '@client/events/version-add-transition.events';

const MIN_BAR_WIDTH = 40;
// Name area height (MuiOutlinedInput height: 46px) — distance from container bottom to bars' bottom edge
const BOTTOM_OFFSET = 46;
const DROP_BORDER = '2px solid #0364e1';

/**
 * Stable empty results object so {@link ProposalBar}'s `useResultsPerGFA` memo
 * (and downstream chart memos) see a steady reference when the proposal has no
 * resultsRecord entry yet — avoids a fresh `{}` allocation on every render.
 */
const EMPTY_RESULTS: Results = Object.freeze({});

type DropSide = 'left' | 'right';
type DropTarget = {
  id: string;
  side: DropSide;
};

type VersionMenuState = {
  versionId: string;
  anchor: HTMLElement;
};

type VersionSelectionOrigin = {
  versionId: string;
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;
  tablistOffset: number;
};

let pendingVersionSelectionOrigin: VersionSelectionOrigin | undefined;

type ProposalBarsTransition = {
  versionId: string;
  direction: 'left' | 'right';
  viewport: HTMLDivElement;
  track: HTMLDivElement;
  width: number;
  height: number;
  topInset: number;
  sourceStage: HTMLElement;
  onActiveChange: (active: boolean) => void;
  timeout: number;
  finish?: () => void;
};

let pendingProposalBarsTransition: ProposalBarsTransition | undefined;
let activeProposalBarsTransition: ProposalBarsTransition | undefined;

const revealLiveProposalBarsStage = () => {
  document
    .querySelector<HTMLElement>('[data-proposal-bars-stage]')
    ?.style.removeProperty('visibility');
};

const removeNewProposalControlFromClone = (stage: HTMLElement) => {
  stage.querySelector<HTMLElement>('[data-new-proposal-control]')?.remove();
};

const startProposalBarsTrackAnimation = (
  transition: ProposalBarsTransition,
) => {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    transition.viewport.remove();
    transition.sourceStage.style.visibility = '';
    revealLiveProposalBarsStage();
    transition.onActiveChange(false);
    window.clearTimeout(transition.timeout);
    if (activeProposalBarsTransition === transition) {
      activeProposalBarsTransition = undefined;
    }
  };
  transition.finish = finish;
  transition.track.addEventListener('transitionend', (event) => {
    if (
      event.target === transition.track &&
      event.propertyName === 'transform'
    ) {
      finish();
    }
  });
  transition.track.style.transition =
    'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)';
  void transition.track.offsetWidth;
  requestAnimationFrame(() => {
    transition.track.style.transform = `translateX(${
      transition.direction === 'right' ? -transition.width : transition.width
    }px)`;
  });
  transition.timeout = window.setTimeout(finish, 700);
};

/**
 * Freeze the currently visible proposal bars as the outgoing version and arm
 * the shared horizontal transition. Existing versions can supply their cached
 * incoming stage immediately; a newly allocated version completes the pending
 * transition from its first live render.
 */
const prepareProposalBarsTransition = (
  versionId: string,
  direction: ProposalBarsTransition['direction'],
  onActiveChange: (active: boolean) => void,
) => {
  const reduceMotion = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (reduceMotion) return;

  activeProposalBarsTransition?.finish?.();
  const barsStage = document.querySelector<HTMLElement>(
    '[data-proposal-bars-stage]',
  );
  if (!barsStage) return;

  const stageRect = barsStage.getBoundingClientRect();
  const topInset = PROPOSALS_OVERVIEW_DIFF_CHIP.heightPx + 2;
  const viewport = document.createElement('div');
  const track = document.createElement('div');
  const liveStageGuard = document.createElement('style');
  const outgoingStage = barsStage.cloneNode(true) as HTMLElement;
  const cachedStage = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-proposal-bars-cache-version]',
    ),
  ).find((stage) => stage.dataset.proposalBarsCacheVersion === versionId);

  viewport.dataset.proposalBarsTransitionViewport = '';
  track.dataset.proposalBarsTransitionTrack = '';
  liveStageGuard.textContent =
    '[data-proposal-bars-stage] { visibility: hidden !important; }';
  outgoingStage.removeAttribute('data-proposal-bars-stage');
  outgoingStage.setAttribute('aria-hidden', 'true');
  outgoingStage.inert = true;
  outgoingStage
    .querySelectorAll<HTMLElement>('[data-proposal-bar]')
    .forEach((bar) => {
      bar.style.animation = 'none';
    });
  removeNewProposalControlFromClone(outgoingStage);
  Object.assign(viewport.style, {
    position: 'fixed',
    left: `${stageRect.left}px`,
    top: `${stageRect.top - topInset}px`,
    width: `${stageRect.width}px`,
    height: `${stageRect.height + topInset}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: '2',
  });
  Object.assign(track.style, {
    position: 'absolute',
    inset: '0',
    width: `${stageRect.width}px`,
    height: `${stageRect.height + topInset}px`,
    transform: 'translateX(0)',
  });
  Object.assign(outgoingStage.style, {
    position: 'absolute',
    left: '0',
    top: `${topInset}px`,
    width: `${stageRect.width}px`,
    height: `${stageRect.height}px`,
    margin: '0',
    visibility: 'visible',
    pointerEvents: 'none',
    animation: 'none',
  });
  track.appendChild(outgoingStage);
  viewport.appendChild(liveStageGuard);
  viewport.appendChild(track);
  document.body.appendChild(viewport);
  barsStage.style.visibility = 'hidden';
  onActiveChange(true);

  const transition: ProposalBarsTransition = {
    versionId,
    direction,
    viewport,
    track,
    width: stageRect.width,
    height: stageRect.height,
    topInset,
    sourceStage: barsStage,
    onActiveChange,
    timeout: 0,
  };
  const abortTransition = () => {
    viewport.remove();
    barsStage.style.visibility = '';
    revealLiveProposalBarsStage();
    onActiveChange(false);
    window.clearTimeout(transition.timeout);
    if (activeProposalBarsTransition === transition) {
      activeProposalBarsTransition = undefined;
    }
    if (pendingProposalBarsTransition === transition) {
      pendingProposalBarsTransition = undefined;
    }
  };
  transition.finish = abortTransition;
  transition.timeout = window.setTimeout(abortTransition, 1500);
  activeProposalBarsTransition = transition;

  if (!cachedStage) {
    pendingProposalBarsTransition = transition;
    return;
  }

  const incomingStage = cachedStage.cloneNode(true) as HTMLElement;
  incomingStage.removeAttribute('data-proposal-bars-cache-version');
  incomingStage.setAttribute('aria-hidden', 'true');
  incomingStage.inert = true;
  Object.assign(incomingStage.style, {
    position: 'absolute',
    left:
      direction === 'right' ? `${stageRect.width}px` : `${-stageRect.width}px`,
    top: `${topInset}px`,
    width: `${stageRect.width}px`,
    height: `${stageRect.height}px`,
    margin: '0',
    visibility: 'visible',
    pointerEvents: 'none',
    animation: 'none',
  });
  track.appendChild(incomingStage);
  startProposalBarsTrackAnimation(transition);
};

type VersionNameFieldProps = {
  name: string;
  editing: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
};

/**
 * The version name stays mounted as the same input in both display and edit
 * modes. A hidden, identically styled copy gives the field its intrinsic width,
 * so focusing it cannot change the pill geometry or move the text.
 */
const VersionNameField: React.FC<VersionNameFieldProps> = ({
  name,
  editing,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);
  const [draft, setDraft] = useState(name);

  useLayoutEffect(() => {
    if (!editing) {
      setDraft(name);
      return;
    }
    cancelledRef.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing, name]);

  const commit = useCallback(() => {
    // A newly created version already carries the name produced by the shared
    // automatic naming helper. Reuse it as both placeholder and submitted
    // fallback rather than ever persisting an empty version name.
    const submittedName = draft.trim().length > 0 ? draft : name;
    if (submittedName !== name) onSave(submittedName);
    else {
      setDraft(name);
      onCancel();
    }
  }, [draft, name, onCancel, onSave]);

  return (
    <Box
      component="span"
      data-value={draft || name || ' '}
      data-version-name=""
      data-version-name-editing={editing || undefined}
      sx={{
        display: 'inline-grid',
        gridTemplateAreas: '"name"',
        alignItems: 'center',
        px: '2px',
        fontSize: '13px',
        fontWeight: 500,
        letterSpacing: '0.06em',
        '&::after': {
          content: 'attr(data-value)',
          gridArea: 'name',
          visibility: 'hidden',
          whiteSpace: 'pre',
        },
      }}
    >
      <Box
        component="input"
        ref={inputRef}
        size={1}
        aria-label="Version name"
        readOnly={!editing}
        tabIndex={editing ? 0 : -1}
        value={draft}
        placeholder={name}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(event.currentTarget.value.replace(/^\s+/g, ''))
        }
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          if (!editing) return;
          if (cancelledRef.current) {
            cancelledRef.current = false;
            return;
          }
          // Undo snackbar sets this so the blur does not commit the draft and
          // replace the version-creation undo with a rename.
          const next = event.relatedTarget;
          if (
            next instanceof Element &&
            next.closest('[data-keep-inline-edit]')
          ) {
            return;
          }
          commit();
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelledRef.current = true;
            setDraft(name);
            onCancel();
            event.currentTarget.blur();
          }
        }}
        sx={{
          gridArea: 'name',
          width: '100%',
          minWidth: 0,
          height: 'calc(1em + 4px)',
          p: 0,
          m: 0,
          boxSizing: 'border-box',
          appearance: 'none',
          border: 0,
          outline: 'none',
          background: 'transparent',
          color: editing
            ? 'rgba(0,0,0,0.8)'
            : 'var(--selected-version-text-color, rgba(0,0,0,0.8))',
          font: 'inherit',
          fontWeight: 'inherit',
          letterSpacing: 'inherit',
          lineHeight: 'normal',
          textTransform: 'inherit',
          textAlign: 'center',
          pointerEvents: editing ? 'auto' : 'none',
          transition: editing ? undefined : 'color 0.15s ease-in-out',
          '&::placeholder': {
            color: 'rgba(0,0,0,0.35)',
            opacity: 1,
          },
        }}
      />
    </Box>
  );
};

type ProposalVersionSwitcherProps = {
  onVersionTransitionActiveChange: (active: boolean) => void;
};

/** Version navigation and management, layered above the proposal bars. */
const ProposalVersionSwitcher: React.FC<ProposalVersionSwitcherProps> = ({
  onVersionTransitionActiveChange,
}) => {
  const version = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  const versions = useVersions();
  const versionIds = useMemo(
    () => versions.map((buildingVersion) => buildingVersion.id),
    [versions],
  );
  const readonly = useIsReadonly();
  const navigateTo = useProjectNavigation();
  const routeVersionId = useRawProjectParams().data.versionId;
  const setSelectedProposalId = useSetSelectedProposalId();
  const addVersion = useAddVersion();
  const applyChanges = useApplyProjectChanges();
  const isConfirmed = useIsConfirmed();
  const project = useProject();
  const updateProject = useUpdateProject();
  const { renamingId, setRenamingId } = useUIState(
    'renamingId',
    'setRenamingId',
  );
  const [versionMenu, setVersionMenu] = useState<VersionMenuState>();
  const [editingVersionId, setEditingVersionId] = useState<string>();
  const switcherRef = useRef<HTMLDivElement>(null);
  const versionOptionsButtonRef = useRef<HTMLButtonElement>(null);
  const previousCaretAnchorRef = useRef<HTMLSpanElement>(null);
  const nextCaretAnchorRef = useRef<HTMLSpanElement>(null);
  const hasAlignedSwitcherRef = useRef(false);
  const [tablistOffset, setTablistOffset] = useState(
    () => pendingVersionSelectionOrigin?.tablistOffset ?? 0,
  );
  const [animateTablist, setAnimateTablist] = useState(
    () => pendingVersionSelectionOrigin !== undefined,
  );
  const [optimisticVersionId, setOptimisticVersionId] = useState<string>();
  const selectedVersionId = optimisticVersionId ?? version?.id;
  const removeVersion = useRemoveVersion({
    selectedVersionId,
    orderedVersionIds: versionIds,
  });

  useEffect(() => {
    const handleOptimisticVersionAdded = (event: Event) => {
      const { versionId } = (
        event as CustomEvent<OptimisticVersionAddedEventDetail>
      ).detail;
      prepareProposalBarsTransition(
        versionId,
        'right',
        onVersionTransitionActiveChange,
      );
    };
    const handleOptimisticVersionAddCancelled = (event: Event) => {
      const { versionId } = (
        event as CustomEvent<OptimisticVersionAddedEventDetail>
      ).detail;
      const transition =
        activeProposalBarsTransition?.versionId === versionId
          ? activeProposalBarsTransition
          : pendingProposalBarsTransition?.versionId === versionId
            ? pendingProposalBarsTransition
            : undefined;
      transition?.finish?.();
    };

    window.addEventListener(
      OPTIMISTIC_VERSION_ADDED_EVENT,
      handleOptimisticVersionAdded,
    );
    window.addEventListener(
      OPTIMISTIC_VERSION_ADD_CANCELLED_EVENT,
      handleOptimisticVersionAddCancelled,
    );
    return () => {
      window.removeEventListener(
        OPTIMISTIC_VERSION_ADDED_EVENT,
        handleOptimisticVersionAdded,
      );
      window.removeEventListener(
        OPTIMISTIC_VERSION_ADD_CANCELLED_EVENT,
        handleOptimisticVersionAddCancelled,
      );
    };
  }, [onVersionTransitionActiveChange]);

  useEffect(() => {
    if (!optimisticVersionId) return;
    if (version?.id === optimisticVersionId) {
      setOptimisticVersionId(undefined);
      return;
    }
    const timeout = window.setTimeout(
      () => setOptimisticVersionId(undefined),
      1500,
    );
    return () => window.clearTimeout(timeout);
  }, [optimisticVersionId, version?.id]);

  const versionMenuTarget = useMemo(
    () =>
      versions.find(
        (buildingVersion) => buildingVersion.id === versionMenu?.versionId,
      ),
    [versionMenu?.versionId, versions],
  );

  const closeVersionMenu = useCallback(() => {
    setVersionMenu(undefined);
  }, []);

  const openVersionMenu = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      versionId: string,
      anchor: HTMLElement = event.currentTarget,
    ) => {
      event.stopPropagation();
      setVersionMenu({ versionId, anchor });
    },
    [],
  );

  const handleVersionContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (readonly || !(event.target instanceof Element)) return;
      const versionTarget =
        event.target.closest<HTMLElement>('[data-version-id]');
      const versionId = versionTarget?.dataset.versionId;
      if (!versionTarget || !versionId) return;
      event.preventDefault();
      event.stopPropagation();
      setVersionMenu({ versionId, anchor: versionTarget });
    },
    [readonly],
  );

  const handleRenameVersion = useCallback(() => {
    if (!versionMenuTarget) return;
    closeVersionMenu();
    if (version?.id !== versionMenuTarget.id) {
      const destinationProposalId = getRememberedProposalForVersion(
        project.id,
        versionMenuTarget.id,
        versionMenuTarget.proposals[0]?.id,
        versionMenuTarget.proposals.map(({ id }) => id),
      );
      navigateTo(
        { versionId: versionMenuTarget.id },
        {
          searchParams: {
            [PROPOSAL_SEARCH_PARAM]: destinationProposalId ?? null,
          },
        },
      );
    }
    setEditingVersionId(versionMenuTarget.id);
  }, [
    closeVersionMenu,
    navigateTo,
    project.id,
    version?.id,
    versionMenuTarget,
  ]);

  const handleSaveVersionName = useCallback(
    async (versionId: string, name: string) => {
      setEditingVersionId(undefined);
      setRenamingId(undefined);
      await applyChanges(
        [{ type: ProjectChangeType.VersionName, versionId, name }],
        {
          amplitude: {
            eventType: AMP_BUILDING_VERSION_UPDATE_NAME,
            eventProperties: { VersionId: versionId },
          },
        },
      );
    },
    [applyChanges, setRenamingId],
  );

  const cancelVersionRename = useCallback(() => {
    setEditingVersionId(undefined);
    setRenamingId(undefined);
  }, [setRenamingId]);

  const handleDuplicateVersion = useCallback(async () => {
    if (!versionMenuTarget) return;
    closeVersionMenu();
    await addVersion(versionMenuTarget, {
      duplicateNameBase: getElementName(versionMenuTarget),
      applyOptions: {
        amplitude: {
          eventType: AMP_BUILDING_VERSION_ADD,
          eventProperties: { VersionId: versionMenuTarget.id },
        },
      },
    });
  }, [addVersion, closeVersionMenu, versionMenuTarget]);

  const handleDeleteVersion = useCallback(async () => {
    if (!versionMenuTarget) return;
    const confirmed = await isConfirmed(
      getRemoveVersionConfirmOptions(getElementName(versionMenuTarget)),
    );
    if (!confirmed) return;

    const destination = getVersionDeleteNavigation(
      versionIds,
      versionMenuTarget.id,
      {
        routeVersionId,
        selectedVersionId,
      },
    );
    const destinationVersion = versions.find(
      ({ id }) => id === destination?.versionId,
    );
    closeVersionMenu();

    // Move off the soon-to-be-invalid URL before the optimistic store update.
    // Otherwise route validation can win the race and fall back to the active
    // version instead of the visible predecessor. Skip when another version is
    // selected so deleting a square does not steal the current selection.
    if (destination && destinationVersion) {
      const proposalIds = destinationVersion.proposals.map(({ id }) => id);
      const destinationProposalId = getRememberedProposalForVersion(
        project.id,
        destination.versionId,
        proposalIds[0],
        proposalIds,
      );
      await navigateTo(
        { versionId: destination.versionId },
        {
          flushSync: destination.flushSync,
          searchParams: {
            [PROPOSAL_SEARCH_PARAM]: destinationProposalId ?? null,
          },
        },
      );
    }

    await removeVersion(versionMenuTarget.id, {
      snackbar: {
        type: 'undo',
        message: 'Version deleted',
      },
      amplitude: {
        eventType: AMP_BUILDING_VERSION_DELETE,
        eventProperties: { VersionId: versionMenuTarget.id },
      },
    });
  }, [
    closeVersionMenu,
    isConfirmed,
    navigateTo,
    removeVersion,
    project.id,
    routeVersionId,
    selectedVersionId,
    versionMenuTarget,
    versionIds,
    versions,
  ]);

  const handleEditBaseline = useCallback(() => {
    if (!versionMenuTarget) return;
    const versionId = versionMenuTarget.id;
    closeVersionMenu();
    if (version?.id !== versionId) {
      void navigateTo(
        { versionId },
        {
          searchParams: {
            [PROPOSAL_SEARCH_PARAM]: BASELINE_PROPOSAL_SEARCH_VALUE,
          },
        },
      );
      return;
    }
    setSelectedProposalId(undefined);
  }, [
    closeVersionMenu,
    navigateTo,
    setSelectedProposalId,
    version?.id,
    versionMenuTarget,
  ]);

  const versionMenuTargetIsActive =
    versionMenuTarget?.id === project.active_version_id;
  const handleSetActiveVersion = useCallback(async () => {
    if (!versionMenuTarget || versionMenuTargetIsActive) return;
    const versionId = versionMenuTarget.id;
    closeVersionMenu();
    await updateProject({
      kind: 'snapshot',
      project: applyVersionMetadata(project, versionId),
    });
  }, [
    closeVersionMenu,
    project,
    updateProject,
    versionMenuTarget,
    versionMenuTargetIsActive,
  ]);

  useLayoutEffect(() => {
    if (!renamingId) return;
    if (!versions.some(({ id }) => id === renamingId)) {
      setRenamingId(undefined);
      return;
    }
    // The route update can trail the optimistic project update. Select the new
    // version in the switcher before paint so its final name is the field that
    // receives focus throughout the transition.
    if (renamingId !== selectedVersionId) setOptimisticVersionId(renamingId);
    setEditingVersionId(renamingId);
  }, [renamingId, selectedVersionId, setRenamingId, versions]);

  const versionLabels = useMemo(
    () =>
      Object.fromEntries(
        versions.map((buildingVersion) => {
          const versionName = getElementName(buildingVersion);
          return [
            buildingVersion.id,
            buildingVersion.id === selectedVersionId ? (
              <Tooltip
                title="Version"
                placement="top"
                describeChild
                disableInteractive
                disableHoverListener={editingVersionId === buildingVersion.id}
              >
                <Box
                  component="span"
                  data-version-id={buildingVersion.id}
                  data-version-menu-trigger=""
                  onClick={(event) => {
                    if (editingVersionId === buildingVersion.id) return;
                    openVersionMenu(
                      event,
                      buildingVersion.id,
                      versionOptionsButtonRef.current ?? event.currentTarget,
                    );
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <VersionNameField
                    key={buildingVersion.id}
                    name={versionName}
                    editing={editingVersionId === buildingVersion.id}
                    onSave={(name) =>
                      handleSaveVersionName(buildingVersion.id, name)
                    }
                    onCancel={cancelVersionRename}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title={versionName} placement="top" disableInteractive>
                <Box
                  component="span"
                  data-version-square=""
                  data-version-id={buildingVersion.id}
                  sx={{
                    position: 'relative',
                    display: 'block',
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    transformOrigin: 'center',
                    transition:
                      'transform 120ms ease-out, background-color 120ms ease-out',
                    // The visible square stays 8x8, while its invisible hitbox
                    // reaches halfway across the 14px tab gap plus the tab's
                    // 6px horizontal padding. Adjacent version hitboxes therefore
                    // meet without changing the rail's visual geometry.
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: '-7px -13px',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      transform: 'scale(1.3)',
                    },
                    '& > span': {
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      p: 0,
                      m: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    },
                  }}
                >
                  <Box component="span">{versionName}</Box>
                </Box>
              </Tooltip>
            ),
          ];
        }),
      ),
    [
      cancelVersionRename,
      editingVersionId,
      handleSaveVersionName,
      openVersionMenu,
      selectedVersionId,
      versions,
    ],
  );

  const handleVersionChange = useCallback(
    (versionId: string) => {
      if (versionId === selectedVersionId) return;
      cancelVersionRename();
      const square = Array.from(
        switcherRef.current?.querySelectorAll<HTMLElement>(
          '[data-version-square]',
        ) ?? [],
      ).find((element) => element.dataset.versionId === versionId);
      const tablist = switcherRef.current?.querySelector<HTMLElement>(
        ':scope > [role="tablist"]',
      );
      const selectedGroupElement = tablist?.querySelector<HTMLElement>(
        '[role="tab"][aria-selected="true"]',
      )?.parentElement;
      const selectedGroup = selectedGroupElement?.getBoundingClientRect();
      let barsEnterDirection: ProposalBarsTransition['direction'] | undefined;
      const projectId = project?.id;
      const sourceVersionId = version?.id;
      const targetVersion = versions.find(
        (candidate) => candidate.id === versionId,
      );
      const targetProposalId =
        projectId !== undefined && targetVersion
          ? getRememberedProposalForVersion(
              projectId,
              versionId,
              targetVersion.proposals[0]?.id,
              targetVersion.proposals.map(({ id }) => id),
            )
          : undefined;
      const proposalToRemember = getProposalToRememberAfterLeavingVersion(
        selectedProposalId,
        version?.proposals?.[0]?.id,
      );
      if (
        projectId !== undefined &&
        sourceVersionId !== undefined &&
        proposalToRemember !== undefined
      ) {
        // Store before navigation starts so a rapid return can pre-render the
        // source version with Proposal 1 selected. This does not alter the
        // already-visible outgoing baseline stage or its animation.
        rememberProposalForVersion(
          projectId,
          sourceVersionId,
          proposalToRemember,
        );
      }
      if (square && selectedGroup) {
        const squareRect = square.getBoundingClientRect();
        barsEnterDirection =
          squareRect.left + squareRect.width / 2 <
          selectedGroup.left + selectedGroup.width / 2
            ? 'left'
            : 'right';
      }
      if (square) {
        const { left, top, width, height } = square.getBoundingClientRect();
        pendingVersionSelectionOrigin = {
          versionId,
          rect: { left, top, width, height },
          tablistOffset,
        };
        const targetGroup =
          square.closest<HTMLElement>('[role="tab"]')?.parentElement;
        const targetPillMeasure = Array.from(
          switcherRef.current?.querySelectorAll<HTMLElement>(
            '[data-version-pill-measure]',
          ) ?? [],
        ).find((element) => element.dataset.versionPillMeasure === versionId);
        if (
          tablist &&
          selectedGroupElement &&
          targetGroup &&
          targetPillMeasure
        ) {
          const children = Array.from(tablist.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement,
          );
          const selectedIndex = children.indexOf(selectedGroupElement);
          const targetIndex = children.indexOf(targetGroup);
          const selectedStyle = window.getComputedStyle(selectedGroupElement);
          const gap = Number.parseFloat(window.getComputedStyle(tablist).gap);
          const selectedMarginLeft = Number.parseFloat(
            selectedStyle.marginLeft,
          );
          const selectedMarginRight = Number.parseFloat(
            selectedStyle.marginRight,
          );
          const inactiveWidth = targetGroup.getBoundingClientRect().width;
          const targetPillWidth =
            targetPillMeasure.getBoundingClientRect().width;
          let finalWidth = 0;
          let targetCenter = 0;

          children.forEach((child, index) => {
            if (index > 0) finalWidth += Number.isFinite(gap) ? gap : 0;
            if (index === selectedIndex) {
              finalWidth += inactiveWidth;
              return;
            }
            if (index === targetIndex) {
              finalWidth += selectedMarginLeft;
              targetCenter = finalWidth + targetPillWidth / 2;
              finalWidth += targetPillWidth + selectedMarginRight;
              return;
            }
            const style = window.getComputedStyle(child);
            finalWidth +=
              Number.parseFloat(style.marginLeft) +
              child.getBoundingClientRect().width +
              Number.parseFloat(style.marginRight);
          });

          // Predict the post-navigation flex layout so the first animation
          // frame already targets the selected pill's final centered position.
          // This keeps the version rail and pre-rendered bars on one timeline.
          setTablistOffset(finalWidth / 2 - targetCenter);
        }
      }
      setOptimisticVersionId(versionId);
      amplitudeLog(AMP_BUILDING_VERSION_SELECT, { VersionId: versionId });
      if (barsEnterDirection) {
        prepareProposalBarsTransition(
          versionId,
          barsEnterDirection,
          onVersionTransitionActiveChange,
        );
      }
      // Commit version and proposal together. Carrying the source version's
      // query value into the destination would briefly select Proposal 1
      // before URL sync restores the remembered destination proposal.
      navigateTo(
        { versionId },
        {
          searchParams: {
            [PROPOSAL_SEARCH_PARAM]: targetProposalId ?? null,
          },
        },
      );
    },
    [
      cancelVersionRename,
      navigateTo,
      onVersionTransitionActiveChange,
      project?.id,
      selectedProposalId,
      selectedVersionId,
      tablistOffset,
      version?.id,
      version?.proposals,
      versions,
    ],
  );
  const handleVersionDoubleClick = useCallback(
    (versionId: string) => {
      if (readonly) return;
      setEditingVersionId(versionId);
      handleVersionChange(versionId);
    },
    [handleVersionChange, readonly],
  );
  const resolvedSelectedVersionId = selectedVersionId ?? version?.id;
  const selectedVersionIndex = resolvedSelectedVersionId
    ? versionIds.indexOf(resolvedSelectedVersionId)
    : -1;
  const previousVersion = versions[selectedVersionIndex - 1];
  const nextVersion = versions[selectedVersionIndex + 1];
  const previousVersionId = previousVersion?.id;
  const nextVersionId = nextVersion?.id;

  const handleAddVersion = useCallback(async () => {
    if (!version) return;
    await addVersion(
      {
        ...version,
        // The plus button starts from the current baseline, not from its
        // proposal workspace. Seed the required action-free proposal with the
        // baseline results so its optimistic first frame already matches the
        // final, enriched proposal instead of flashing the gray backdrop.
        actions: [],
        proposals: [{ resultsRecord: version.resultsRecord }],
      },
      {
        applyOptions: {
          amplitude: {
            eventType: AMP_BUILDING_VERSION_ADD,
            eventProperties: { VersionId: version.id },
          },
        },
      },
    );
  }, [addVersion, version]);

  useLayoutEffect(() => {
    const switcher = switcherRef.current;
    const tablist = switcher?.querySelector<HTMLElement>(
      ':scope > [role="tablist"]',
    );
    const selectedTab = tablist?.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );
    const selectedGroup = selectedTab?.parentElement;
    if (!switcher || !tablist || !selectedGroup) return;

    const getRenderedTranslateX = (element: HTMLElement) => {
      const transform = window.getComputedStyle(element).transform;
      if (transform === 'none') return 0;
      const values = transform
        .slice(transform.indexOf('(') + 1, -1)
        .split(',')
        .map(Number);
      const translateX = Number(
        transform.startsWith('matrix3d') ? values[12] : values[4],
      );
      return Number.isFinite(translateX) ? translateX : 0;
    };

    const alignSelectedVersion = () => {
      const switcherRect = switcher.getBoundingClientRect();
      const selectedRect = selectedGroup.getBoundingClientRect();
      const selectedCenterWithoutTransform =
        selectedRect.left +
        selectedRect.width / 2 -
        getRenderedTranslateX(tablist) -
        getRenderedTranslateX(selectedGroup);
      const desiredOffset =
        switcherRect.left +
        switcherRect.width / 2 -
        selectedCenterWithoutTransform;
      setTablistOffset((currentOffset) =>
        Math.abs(currentOffset - desiredOffset) < 0.25
          ? currentOffset
          : desiredOffset,
      );
    };

    const selectionOrigin = pendingVersionSelectionOrigin;
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    let revealTimeout: number | undefined;
    if (
      selectionOrigin &&
      selectedVersionId &&
      selectionOrigin.versionId === selectedVersionId &&
      !reduceMotion
    ) {
      const selectedRect = selectedGroup.getBoundingClientRect();
      const translateX =
        selectionOrigin.rect.left +
        selectionOrigin.rect.width / 2 -
        (selectedRect.left + selectedRect.width / 2);
      const translateY =
        selectionOrigin.rect.top +
        selectionOrigin.rect.height / 2 -
        (selectedRect.top + selectedRect.height / 2);
      const verticalInset = Math.max((selectedRect.height - 8) / 2, 0);
      selectedGroup.style.setProperty('--version-reveal-x', `${translateX}px`);
      selectedGroup.style.setProperty('--version-reveal-y', `${translateY}px`);
      selectedGroup.style.setProperty(
        '--version-reveal-inset',
        `${verticalInset}px`,
      );
      selectedGroup.removeAttribute('data-version-revealing');
      void selectedGroup.offsetWidth;
      selectedGroup.dataset.versionRevealing = '';
      revealTimeout = window.setTimeout(() => {
        selectedGroup.removeAttribute('data-version-revealing');
      }, 240);
    }
    pendingVersionSelectionOrigin = undefined;

    alignSelectedVersion();
    if (!hasAlignedSwitcherRef.current) {
      hasAlignedSwitcherRef.current = true;
      requestAnimationFrame(() => setAnimateTablist(true));
    }
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(alignSelectedVersion);
    observer?.observe(switcher);
    observer?.observe(tablist);
    observer?.observe(selectedGroup);
    return () => {
      observer?.disconnect();
      if (revealTimeout !== undefined) window.clearTimeout(revealTimeout);
    };
  }, [selectedVersionId, versionIds.length]);

  if (!version || versionIds.length === 0) return null;

  return (
    <Box
      ref={switcherRef}
      data-version-switcher=""
      onContextMenu={handleVersionContextMenu}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pointerEvents: 'none',
        '& > [role="tablist"]': {
          gap: '14px',
          height: '31px !important',
          pointerEvents: 'auto',
          transform: `translateX(${tablistOffset}px)`,
          transition: animateTablist
            ? 'transform 240ms cubic-bezier(0.2, 0, 0, 1)'
            : 'none',
        },
        '@keyframes versionPillReveal': {
          from: {
            transform:
              'translate(var(--version-reveal-x), var(--version-reveal-y))',
            clipPath:
              'inset(var(--version-reveal-inset) calc(50% - 4px) round 2px)',
          },
          to: {
            transform: 'translate(0, 0)',
            clipPath: 'inset(0 round 5px)',
          },
        },
        '& [role="tab"]': {
          px: '6px',
          height: '22px',
          fontSize: '13px',
        },
        '& [role="tab"][aria-selected="true"]': {
          fontSize: '13px',
          fontWeight: 500,
        },
        '& > [role="tablist"] > div': {
          position: 'relative',
          gap: 0,
          height: '22px',
          pr: 0,
          '& [data-version-options]': {
            opacity: 'var(--version-controls-opacity, 0)',
            // Keep the transparent vertical bridge interactive even before the
            // icon is visible, so entering between the name and icon activates
            // the same menu target without a dead zone.
            pointerEvents: 'auto',
          },
          '&:hover [data-version-options], & [data-version-options]:focus-within':
            {
              opacity: 1,
            },
          // A clicked version tab keeps focus after navigation. Styling the
          // whole group's :focus-within as hover would therefore leave the
          // options button highlighted until the user clicked elsewhere.
          '&:hover [data-version-options] button, & [data-version-options] button:focus-visible':
            {
              color: 'rgba(0,0,0,0.8)',
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
          '&:has([data-version-square]):hover': {
            backgroundColor: 'transparent',
          },
          '&:has([data-version-square])': {
            opacity: 'var(--version-controls-opacity, 0)',
            pointerEvents: 'var(--version-controls-pointer-events, none)',
            transition: 'opacity 0.15s ease-in-out',
          },
        },
        '& > [role="tablist"] > div:has([role="tab"][aria-selected="true"])': {
          backgroundColor: 'var(--selected-version-background, transparent)',
          mx: '6px',
          // Expanded square hitboxes may reach the selected pill. Keep the
          // selected version above them so only one tooltip owns the pointer.
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'var(--selected-version-background, transparent)',
          },
        },
        '& > [role="tablist"] > div[data-version-revealing]': {
          animation: 'versionPillReveal 240ms cubic-bezier(0.2, 0, 0, 1) both',
        },
        '& > [role="tablist"] > div:has([data-version-name-editing="true"])': {
          backgroundColor: '#fff !important',
          outline: '1px solid',
          outlineColor: 'custom.focus',
          outlineOffset: '-1px',
          '& [data-version-options]': {
            opacity: '0 !important',
            pointerEvents: 'none',
          },
          '&:hover': {
            backgroundColor: '#fff !important',
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& > [role="tablist"]': {
            transition: 'none',
          },
          '& > [role="tablist"] > div[data-version-revealing]': {
            animation: 'none',
          },
        },
      }}
    >
      <PillTabs
        tabs={versionIds}
        value={selectedVersionId ?? version.id}
        labels={versionLabels}
        onChange={handleVersionChange}
        onDoubleClick={handleVersionDoubleClick}
        labelComponent="span"
        groupClickable
        groupContent={(versionId) => {
          if (readonly || versionId !== selectedVersionId) return null;
          const versionName = getElementName(
            versions.find(({ id }) => id === versionId),
          );
          return (
            <Box
              component="span"
              data-version-options=""
              onClick={(event) =>
                openVersionMenu(
                  event,
                  versionId,
                  versionOptionsButtonRef.current ?? event.currentTarget,
                )
              }
              sx={{
                display: 'inline-flex',
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                pt: '10px',
                transition: 'opacity 0.15s ease-in-out',
              }}
            >
              <Tooltip
                title="Version options"
                placement="bottom"
                disableInteractive
              >
                <IconButton
                  ref={versionOptionsButtonRef}
                  category="mui"
                  icon="MoreHoriz"
                  size={16}
                  shape="square"
                  disableRipple
                  aria-label={`${versionName} options`}
                  onClick={(event) => openVersionMenu(event, versionId)}
                  sx={{
                    width: 16,
                    height: 16,
                    p: 0,
                    color: 'rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          );
        }}
        size="default"
      >
        {!readonly && (
          <Tooltip title="New version" placement="top" disableInteractive>
            <IconButton
              data-new-version=""
              category="mui"
              icon="Add"
              size={13}
              shape="square"
              disableRipple
              aria-label="New version"
              onClick={handleAddVersion}
              sx={{
                position: 'relative',
                zIndex: 2,
                width: 20,
                height: 20,
                p: 0,
                color: 'rgba(0,0,0,0.3)',
                // Fade in with the chart hover. Pointer events follow the same
                // hover: z-index keeps the button above the tablist while it
                // is shown, and none while it is invisible so the spot cannot
                // add a version.
                opacity: 'var(--version-controls-opacity, 0)',
                pointerEvents: 'var(--version-controls-pointer-events, none)',
                transition: 'opacity 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  color: 'rgba(0,0,0,0.85)',
                },
              }}
            />
          </Tooltip>
        )}
      </PillTabs>
      {versions.length > 1 && (
        <>
          <Box
            ref={previousCaretAnchorRef}
            component="span"
            aria-hidden="true"
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              width: 24,
              height: 24,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <Tooltip
            title={
              previousVersion
                ? `Previous version: ${getElementName(previousVersion)}`
                : ''
            }
            placement="right"
            disableInteractive
            slotProps={{
              popper: {
                anchorEl: () => previousCaretAnchorRef.current!,
              },
            }}
          >
            <IconButton
              data-version-navigation="previous"
              category="mui"
              icon="KeyboardArrowLeft"
              size={24}
              disabled={!previousVersionId}
              disableRipple
              aria-label="Previous version"
              onClick={(event) => {
                event.stopPropagation();
                if (previousVersionId) handleVersionChange(previousVersionId);
              }}
              sx={{
                position: 'absolute',
                left: 0,
                top: '25%',
                width: 'var(--previous-version-trigger-width, 18%)',
                height: '50%',
                p: 0,
                pl: '8px',
                justifyContent: 'flex-start',
                borderRadius: 0,
                color: 'rgba(0,0,0,0.3)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'rgba(0,0,0,0.8)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(0,0,0,0.1)',
                },
              }}
            />
          </Tooltip>
          <Box
            ref={nextCaretAnchorRef}
            component="span"
            aria-hidden="true"
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              width: 24,
              height: 24,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <Tooltip
            title={
              nextVersion ? `Next version: ${getElementName(nextVersion)}` : ''
            }
            placement="left"
            disableInteractive
            slotProps={{
              popper: {
                anchorEl: () => nextCaretAnchorRef.current!,
              },
            }}
          >
            <IconButton
              data-version-navigation="next"
              category="mui"
              icon="KeyboardArrowRight"
              size={24}
              disabled={!nextVersionId}
              disableRipple
              aria-label="Next version"
              onClick={(event) => {
                event.stopPropagation();
                if (nextVersionId) handleVersionChange(nextVersionId);
              }}
              sx={{
                position: 'absolute',
                right: 0,
                top: '25%',
                width: 'var(--next-version-trigger-width, 18%)',
                height: '50%',
                p: 0,
                pr: '8px',
                justifyContent: 'flex-end',
                borderRadius: 0,
                color: 'rgba(0,0,0,0.3)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'rgba(0,0,0,0.8)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(0,0,0,0.1)',
                },
              }}
            />
          </Tooltip>
        </>
      )}
      <Box
        aria-hidden="true"
        sx={{
          position: 'fixed',
          left: -100000,
          top: 0,
          display: 'flex',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {versions.map((buildingVersion) => (
          <Box
            key={buildingVersion.id}
            component="span"
            data-version-pill-measure={buildingVersion.id}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: '22px',
              px: '6px',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            <VersionNameField
              name={getElementName(buildingVersion)}
              editing={false}
              onSave={() => undefined}
              onCancel={() => undefined}
            />
          </Box>
        ))}
      </Box>
      <Menu
        open={Boolean(versionMenuTarget)}
        anchorEl={versionMenu?.anchor}
        onClose={closeVersionMenu}
        disableRestoreFocus
      >
        <Menu.Group
          label={
            versionMenuTarget ? getElementName(versionMenuTarget) : undefined
          }
        >
          <Menu.Item
            icon={<Icon category="mui" icon="Edit" />}
            onClick={handleRenameVersion}
          >
            Rename
          </Menu.Item>
          <Menu.Item
            icon={<Icon category="mui" icon="FileCopy" />}
            onClick={handleDuplicateVersion}
          >
            Duplicate
          </Menu.Item>
          <Menu.Item
            icon={<Icon category="mui" icon="DeleteForever" />}
            disabled={versions.length <= 1}
            onClick={handleDeleteVersion}
          >
            Delete
          </Menu.Item>
          <Menu.Item
            icon={<Icon category="mui" icon="EditOutlined" />}
            onClick={handleEditBaseline}
          >
            Edit Baseline
          </Menu.Item>
        </Menu.Group>
        <Menu.Divider />
        <Menu.Item
          icon={
            versionMenuTargetIsActive ? (
              <Icon category="mui" icon="Check" />
            ) : undefined
          }
          disabled={versionMenuTargetIsActive}
          onClick={handleSetActiveVersion}
        >
          {versionMenuTargetIsActive
            ? 'Active version'
            : 'Set as active version'}
        </Menu.Item>
      </Menu>
    </Box>
  );
};

type PreRenderedProposalBarProps = {
  name: string;
  nameStyle?: 'proposal' | 'baseline';
  color: string;
  results: Results;
  barWidth: number;
  maxBarHeight: number;
  co2ePerGfaMax: number;
  costPerGfaMax: number;
  baselineCo2ePerGFA?: number;
  baselineCostPerGFA?: number;
  outlineOnly?: boolean;
  active: boolean;
  dimmed?: boolean;
  showCo2LabelIcon: boolean;
  tooltipPlacement: 'left' | 'right';
};

/** Non-interactive bar used only by the off-screen version transition cache. */
const PreRenderedProposalBar: React.FC<PreRenderedProposalBarProps> = ({
  name,
  nameStyle = 'proposal',
  color,
  results,
  barWidth,
  maxBarHeight,
  co2ePerGfaMax,
  costPerGfaMax,
  baselineCo2ePerGFA,
  baselineCostPerGFA,
  outlineOnly,
  active,
  dimmed,
  showCo2LabelIcon,
  tooltipPlacement,
}) => {
  const target = useResolvedProjectTarget();
  const displayCurrency = useDisplayCurrency();
  const resultsPerGFA = useResultsPerGFA(results);
  const co2ePerGFA = getConversionFactorValue(resultsPerGFA, 'co2e');
  const costPerGFA = getConversionFactorValue(resultsPerGFA, 'sek');
  const costBarHeight =
    displayCurrency.isEnabled && costPerGfaMax > 0
      ? (costPerGFA / costPerGfaMax) * maxBarHeight
      : 0;
  const backdropCostBarHeight =
    displayCurrency.isEnabled &&
    costPerGfaMax > 0 &&
    baselineCostPerGFA !== undefined
      ? (baselineCostPerGFA / costPerGfaMax) * maxBarHeight
      : 0;
  const costLabel =
    displayCurrency.isEnabled && costPerGFA > 0
      ? getDisplayValueData(
          { value: costPerGFA, unit: 'sek' },
          { outputUnit: displayCurrency.current, notation: 'compact' },
        ).value
      : undefined;

  return (
    <OverviewBarShell
      barWidth={barWidth}
      maxBarHeight={maxBarHeight}
      color={color}
      co2eChartData={getCo2eChartDataFromResults(resultsPerGFA)}
      co2ePerGFA={co2ePerGFA}
      co2ePerGfaMax={co2ePerGfaMax}
      backdropCo2ePerGFA={baselineCo2ePerGFA}
      backdropColor={baselineCo2ePerGFA ? BASELINE_COLOR : undefined}
      backdropCostBarHeight={backdropCostBarHeight}
      costBarHeight={costBarHeight}
      costLabel={costLabel}
      showInnerBorders
      innerBorderColor={
        nameStyle === 'baseline'
          ? PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR
          : undefined
      }
      outlineOnly={outlineOnly}
      showCo2LabelIcon={showCo2LabelIcon}
      tooltipPlacement={tooltipPlacement}
      diffValue={
        hasVisibleProposalTarget(target) ? co2ePerGFA - target : undefined
      }
      active={active}
      isSelected={active}
      dimmed={dimmed}
      cursor="default"
      name={name}
      nameStyle={nameStyle}
    />
  );
};

type PreRenderedProposalBarsStageProps = {
  version: Baseline;
  containerWidth: number;
  maxBarHeight: number;
};

/**
 * Keeps a lightweight, non-interactive bar stage ready for an inactive version.
 * Only this visual stage is pre-rendered; editors, menus and other overview UI
 * remain mounted once for the selected version.
 */
const PreRenderedProposalBarsStage: React.FC<
  PreRenderedProposalBarsStageProps
> = ({ version, containerWidth, maxBarHeight }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const showCo2LabelIcon = useSharedCo2LabelIconVisibility(
    stageRef,
    version.id,
  );
  const proposals = useMemo(() => getProposals(version), [version]);
  const records = useProposalsResultRecords(true, true, version);
  const getResultsPerGFA = useGetResultsPerGFA();
  const target = useResolvedProjectTarget();
  const projectId = useProjectId();
  const selectedProposalId = getRememberedProposalForVersion(
    projectId,
    version.id,
    proposals[0]?.id,
    proposals.map(({ id }) => id),
  );
  const isProposalSelected = proposals.some(
    ({ id }) => id === selectedProposalId,
  );
  const effectiveColors = useMemo(
    () => buildProposalEffectiveColorIndexMap(proposals),
    [proposals],
  );
  const barWidth = useMemo(() => {
    const count = proposals.length + (isProposalSelected ? 0 : 1);
    if (containerWidth === 0) return maxBarHeight * 0.6;
    const maxBarWidth = Math.min(containerWidth * 0.25, 150);
    return Math.max(
      MIN_BAR_WIDTH,
      Math.min((2 * containerWidth) / (3 * count + 1), maxBarWidth),
    );
  }, [containerWidth, isProposalSelected, maxBarHeight, proposals.length]);
  const co2ePerGfaMax = useMemo(
    () =>
      computeProposalOverviewCo2PerGfaScaleMax(
        version.id,
        records as Record<string, ResultsRecord>,
        proposals,
        getResultsPerGFA,
        { targetsKgCo2ePerM2: [target] },
      ),
    [getResultsPerGFA, proposals, records, target, version.id],
  );
  const costPerGfaMax = useMemo(
    () =>
      computeProposalOverviewCostPerGfaScaleMax(
        version.id,
        records as Record<string, ResultsRecord>,
        proposals,
        getResultsPerGFA,
      ),
    [getResultsPerGFA, proposals, records, version.id],
  );
  const baselineResultsPerGFA = getResultsPerGFA(
    records[version.id]?.[version.id] ?? EMPTY_RESULTS,
  );
  const baselineCo2ePerGFA = getConversionFactorValue(
    baselineResultsPerGFA,
    'co2e',
  );
  const baselineCostPerGFA = getConversionFactorValue(
    baselineResultsPerGFA,
    'sek',
  );

  return (
    <Box
      ref={stageRef}
      data-proposal-bars-cache-version={version.id}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        width: '100%',
        flexShrink: 0,
        pointerEvents: 'none',
      }}
    >
      {!isProposalSelected && (
        <PreRenderedProposalBar
          name={getElementName(version)}
          nameStyle="baseline"
          color={PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR}
          results={records[version.id]?.[version.id] ?? EMPTY_RESULTS}
          barWidth={barWidth}
          maxBarHeight={maxBarHeight}
          co2ePerGfaMax={co2ePerGfaMax}
          costPerGfaMax={costPerGfaMax}
          active
          showCo2LabelIcon={showCo2LabelIcon}
          tooltipPlacement="right"
        />
      )}
      {proposals.map((proposal: IProposal, index) => (
        <PreRenderedProposalBar
          key={proposal.id}
          name={proposal.name}
          color={getProposalSwatchColor(
            effectiveColors.get(proposal.id) ?? proposal.colorIndex,
          )}
          results={records[proposal.id]?.[version.id] ?? EMPTY_RESULTS}
          barWidth={barWidth}
          maxBarHeight={maxBarHeight}
          co2ePerGfaMax={co2ePerGfaMax}
          costPerGfaMax={costPerGfaMax}
          baselineCo2ePerGFA={baselineCo2ePerGFA}
          baselineCostPerGFA={baselineCostPerGFA}
          outlineOnly={proposal.actionIds.length === 0}
          active={selectedProposalId === proposal.id}
          dimmed={selectedProposalId === undefined}
          showCo2LabelIcon={showCo2LabelIcon}
          tooltipPlacement={index * 2 < proposals.length ? 'right' : 'left'}
        />
      ))}
    </Box>
  );
};

/**
 * proposals overview — same bar chart layout as {@link ProposalsOverview}
 * but renders {@link ProposalBar} for each {@link IProposal} instead of
 * v1 {@link ProposalBar}. Baseline is shown first only while selected.
 */
type ProposalsProps = {
  onDeselect?: React.MouseEventHandler<HTMLDivElement>;
  onDeselectGroup?: () => void;
};

const Proposals: React.FC<ProposalsProps> = ({
  onDeselect,
  onDeselectGroup,
}) => {
  const version = useSelectedVersion();
  const versions = useVersions();
  const proposals = useProposals();
  const selectedProposalId = useSelectedProposalId();
  const versionId = version?.id ?? '';
  const [optimisticProposalSelection, setOptimisticProposalSelection] =
    useState<{ versionId: string; proposalId: ProposalId }>();
  const [versionTransitionActive, setVersionTransitionActive] = useState(false);
  const displayedSelectedProposalId =
    optimisticProposalSelection?.versionId === versionId
      ? optimisticProposalSelection.proposalId
      : selectedProposalId;
  const setProposalSelectionImmediately = useCallback(
    (proposalId: ProposalId | undefined) => {
      setOptimisticProposalSelection(
        proposalId ? { versionId, proposalId } : undefined,
      );
    },
    [versionId],
  );

  // Drop the optimistic overlay once the URL catches up, when another
  // explicit proposal is addressed, or when a different version replaces the chart.
  useEffect(() => {
    setOptimisticProposalSelection((current) => {
      if (!current) return current;
      if (
        current.versionId !== versionId ||
        current.proposalId === selectedProposalId
      ) {
        return undefined;
      }
      // A resolved URL id that is not the preview means the address bar moved
      // on. Falling back while the new id is not in the store yet leaves
      // selectedProposalId on the previous proposal, so that case stays.
      const rawId = proposalIdFromSearchValue(
        getSelectedProposalSearchValueFromUrl(),
      );
      if (
        rawId &&
        rawId === selectedProposalId &&
        rawId !== current.proposalId
      ) {
        return undefined;
      }
      return current;
    });
  }, [selectedProposalId, versionId]);
  // Baseline is an explicit URL state, not a fallback for a stale proposal id.
  // During an optimistic version delete the store can expose the destination
  // version one render before its atomic route update lands. Treating the old
  // version's proposal id as baseline in that frame creates a phantom empty
  // Baseline slot in the chart.
  const showBaseline = displayedSelectedProposalId === undefined;
  const target = useResolvedProjectTarget();
  const targetLabel = useScopeTargetLabel();
  const readonly = useIsReadonly();
  const addProposal = useAddProposal(setProposalSelectionImmediately);
  const reorderProposals = useReorderProposals();
  useDeleteProposalShortcut();
  // Local display order — updated immediately on drop, synced when proposals change externally.
  const [orderedIds, setOrderedIds] = useState<string[]>(() =>
    proposals.map((p) => p.id),
  );
  const proposalsById = useMemo(
    () => new Map(proposals.map((p) => [p.id, p])),
    [proposals],
  );
  // Sync when proposals are added/removed from the store.
  useEffect(() => {
    setOrderedIds((prev) => {
      const incoming = proposals.map((p) => p.id);
      const prevSet = new Set(prev);
      const incomingSet = new Set(incoming);
      const sameIds =
        prev.length === incoming.length &&
        incoming.every((id) => prevSet.has(id));
      if (sameIds) return prev;
      const added = incoming.filter((id) => !prevSet.has(id));
      // If the added ids are not all at the tail of incoming, the store order is
      // authoritative (e.g. undo restoring a deleted proposal at its original
      // position). Respect incoming order in that case.
      const addedSet = new Set(added);
      const incomingTail = incoming.slice(incoming.length - added.length);
      const addedAreAtTail =
        added.length > 0 &&
        incomingTail.every((id) => addedSet.has(id)) &&
        added.every((id) => addedSet.has(id));
      if (!addedAreAtTail) return incoming;
      // Normal add: keep existing display order and append new ones at the end.
      const kept = prev.filter((id) => incomingSet.has(id));
      return [...kept, ...added];
    });
  }, [proposals]);

  const orderedProposals = useMemo(() => {
    const ordered = orderedIds
      .map((id) => proposalsById.get(id))
      .filter((p) => p !== undefined);
    const orderedSet = new Set(orderedIds);

    // A version switch can replace the entire proposal set before the sync
    // effect above has updated local drag order. Include new ids immediately
    // so the layout-effect transition clones the complete incoming chart,
    // rather than a baseline-only first render that swaps after animation.
    return [
      ...ordered,
      ...proposals.filter((proposal) => !orderedSet.has(proposal.id)),
    ];
  }, [orderedIds, proposals, proposalsById]);

  const draggingIdRef = useRef<string | null>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);
  const barRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const [dropTarget, setDropTargetState] = useState<DropTarget | null>(null);

  const setDropTarget = useCallback((next: DropTarget | null) => {
    dropTargetRef.current = next;
    setDropTargetState(next);
  }, []);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, id: string) => {
      draggingIdRef.current = id;
      e.dataTransfer.effectAllowed = 'move';
      document.body.dataset.dragging = 'true';

      const el = barRefsMap.current.get(id);
      if (!el) return;

      const clone = el.cloneNode(true) as HTMLDivElement;
      clone.style.position = 'fixed';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.pointerEvents = 'none';
      clone
        .querySelectorAll('[data-drag-hide="true"]')
        .forEach((node) => node.remove());
      document.body.appendChild(clone);

      const rect = el.getBoundingClientRect();
      e.dataTransfer.setDragImage(
        clone,
        e.clientX - rect.left,
        e.clientY - rect.top,
      );

      // Remove after the browser has captured the drag image.
      requestAnimationFrame(() => document.body.removeChild(clone));
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, id: string) => {
      e.preventDefault();
      const draggingId = draggingIdRef.current;
      if (!draggingId || draggingId === id) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const relX = (e.clientX - rect.left) / rect.width;
      const side: DropSide = relX < 0.5 ? 'left' : 'right';
      const prev = dropTargetRef.current;
      if (prev?.id !== id || prev.side !== side) {
        setDropTarget({ id, side });
      }
    },
    [setDropTarget],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setDropTarget(null);
    },
    [setDropTarget],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropId: string) => {
      e.preventDefault();
      const fromId = draggingIdRef.current;
      const currentDropTarget = dropTargetRef.current;
      draggingIdRef.current = null;
      setDropTarget(null);

      if (!fromId || fromId === dropId) return;

      const fromIndex = orderedIds.indexOf(fromId);
      if (fromIndex === -1) return;
      const reordered = [...orderedIds];
      reordered.splice(fromIndex, 1);
      const toIndex = reordered.indexOf(dropId);
      if (toIndex === -1) return;
      const side =
        currentDropTarget?.id === dropId ? currentDropTarget.side : 'left';
      const insertAt = side === 'left' ? toIndex : toIndex + 1;
      reordered.splice(insertAt, 0, fromId);
      setOrderedIds(reordered);
      reorderProposals(reordered);
    },
    [setDropTarget, reorderProposals, orderedIds],
  );

  const handleDragEnd = useCallback(() => {
    draggingIdRef.current = null;
    setDropTarget(null);
    delete document.body.dataset.dragging;
  }, [setDropTarget]);

  const handleAddProposal = useCallback(() => {
    addProposal();
  }, [addProposal]);

  const handleOverviewMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const { top, height } = event.currentTarget.getBoundingClientRect();
      event.currentTarget.toggleAttribute(
        'data-version-options-hover-active',
        event.clientY < top + height / 2,
      );
    },
    [],
  );

  const handleOverviewMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.currentTarget.removeAttribute('data-version-options-hover-active');
    },
    [],
  );

  const { co2ePerGfaMax, costPerGfaMax } = useProposalsBarChartValues(
    undefined,
    {
      includeProjectTargetInCo2Scale: true,
    },
  );

  // Lifecycle-filtered results for every proposal (keyed by proposal.id).
  // Reading raw `proposal.resultsRecord` here would skip the lifecycle filter,
  // so the bars wouldn't react to LifecycleSelect toggles (matches v1, which
  // goes via useProposalsBarChartValues → useProposalResultsRecord).
  const proposalsResultRecords = useProposalsResultRecords();

  const baselineResults =
    proposalsResultRecords[versionId]?.[versionId] ?? EMPTY_RESULTS;
  const baselineResultsPerGFA = useResultsPerGFA(baselineResults);
  const baselineCo2ePerGFA = getConversionFactorValue(
    baselineResultsPerGFA,
    'co2e',
  );
  const baselineCostPerGFA = getConversionFactorValue(
    baselineResultsPerGFA,
    'sek',
  );

  // Effective palette index per row (legacy colorIndex 0) before persistence catches up
  const proposalBarColorIndexById = useMemo(
    () => buildProposalEffectiveColorIndexMap(proposals),
    [proposals],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const barsStageRef = useRef<HTMLDivElement>(null);
  const showCo2LabelIcon = useSharedCo2LabelIconVisibility(
    barsStageRef,
    versionId,
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useLayoutEffect(() => {
    const pendingTransition = pendingProposalBarsTransition;
    const activeTransition = activeProposalBarsTransition;
    const transition =
      pendingTransition?.versionId === versionId
        ? pendingTransition
        : activeTransition?.versionId === versionId
          ? activeTransition
          : undefined;
    const stage = barsStageRef.current;
    if (!transition || !stage) return;

    stage.style.visibility = 'hidden';

    // A cache hit already supplied the incoming clone and started the track
    // before navigation. Keep the newly mounted live stage hidden behind that
    // clone until the transition finishes, but do not append it a second time.
    if (transition !== pendingTransition) return;

    window.clearTimeout(transition.timeout);
    const incomingStage = stage.cloneNode(true) as HTMLElement;
    incomingStage.removeAttribute('data-proposal-bars-stage');
    incomingStage.setAttribute('aria-hidden', 'true');
    incomingStage.inert = true;
    incomingStage
      .querySelectorAll<HTMLElement>('[data-proposal-bar]')
      .forEach((bar) => {
        bar.style.animation = 'none';
      });
    removeNewProposalControlFromClone(incomingStage);
    Object.assign(incomingStage.style, {
      position: 'absolute',
      left:
        transition.direction === 'right'
          ? `${transition.width}px`
          : `${-transition.width}px`,
      top: `${transition.topInset}px`,
      width: `${transition.width}px`,
      height: `${transition.height}px`,
      margin: '0',
      visibility: 'visible',
      pointerEvents: 'none',
      animation: 'none',
    });
    transition.track.appendChild(incomingStage);
    startProposalBarsTrackAnimation(transition);
    pendingProposalBarsTransition = undefined;
  }, [versionId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Match {@link ProposalsOverview}: ~30% viewport minus label/name band
  const maxBarHeight = Math.round(windowHeight * 0.4 - 48);
  const displayCurrency = useDisplayCurrency();
  const baselineCostBarHeight =
    displayCurrency.isEnabled && costPerGfaMax > 0
      ? (baselineCostPerGFA / costPerGfaMax) * maxBarHeight
      : 0;

  // Match {@link ProposalsOverview} sizing: solve barWidth so that with
  // space-evenly gaps, gap = barWidth/2. Baseline participates in the layout
  // only while it is selected; a selected proposal removes it entirely.
  const barWidth = useMemo(() => {
    const n = proposals.length + (showBaseline ? 1 : 0);
    if (containerWidth === 0) return maxBarHeight * 0.6;
    const MAX_BAR_WIDTH = Math.min(containerWidth * 0.25, 150);
    return Math.max(
      MIN_BAR_WIDTH,
      Math.min((2 * containerWidth) / (3 * n + 1), MAX_BAR_WIDTH),
    );
  }, [containerWidth, maxBarHeight, proposals.length, showBaseline]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const stage = barsStageRef.current;
    if (!container || !stage) return;
    const bars = Array.from(
      stage.querySelectorAll<HTMLElement>('[data-proposal-bar]'),
    );
    if (bars.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const barRects = bars.map((bar) => bar.getBoundingClientRect());
    const firstBarLeft = Math.min(...barRects.map(({ left }) => left));
    const lastBarRight = Math.max(...barRects.map(({ right }) => right));
    const previousTriggerWidth = Math.max(
      40,
      (firstBarLeft - containerRect.left) / 3,
    );
    const nextTriggerWidth = Math.max(
      40,
      (containerRect.right - lastBarRight) / 3,
    );

    container.style.setProperty(
      '--previous-version-trigger-width',
      `${previousTriggerWidth}px`,
    );
    container.style.setProperty(
      '--next-version-trigger-width',
      `${nextTriggerWidth}px`,
    );
  }, [barWidth, containerWidth, showBaseline, orderedProposals, versionId]);

  // The line is a view of the scope's target, so clicking it opens the scope
  // overlay with that field ready to type in.
  const handleTargetLineClick = useCallback<React.MouseEventHandler>(
    (event) => {
      event.stopPropagation();
      requestScopeOverlay({ focusTarget: true });
    },
    [],
  );

  // One line, for the target the selected scope is measured against.
  const targetLineBottom =
    hasVisibleProposalTarget(target) && co2ePerGfaMax > 0
      ? Math.round((target / co2ePerGfaMax) * maxBarHeight) + BOTTOM_OFFSET
      : null;

  // Reads as the scope selector's own target field, so the line is recognisable
  // as a view of it: the same cloud and level name.
  const targetTooltip = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box component="span">Target:</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Icon category="costEmission" icon="kgCO2e" size={16} />
          <Box component="span">
            {targetLabel.levelName
              ? `${targetLabel.levelName} (${targetLabel.value})`
              : targetLabel.value}
          </Box>
        </Box>
      </Box>
    ),
    [targetLabel],
  );

  return (
    <Box
      ref={containerRef}
      data-proposals-overview=""
      onClick={onDeselect}
      onMouseMove={handleOverviewMouseMove}
      onMouseLeave={handleOverviewMouseLeave}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 0,
        pt: 32,
        pb: 0,
        overflowX: 'auto',
        // `visible` computes to `auto` when the other axis scrolls. A
        // fractional one-pixel height difference then gives the proposals
        // overview its own vertical scrollbar beside MainLayout's fade rail.
        overflowY: 'hidden',
        '&:hover [data-version-navigation]': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      <ProposalVersionSwitcher
        onVersionTransitionActiveChange={setVersionTransitionActive}
      />
      {containerWidth > 0 && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            left: -100000,
            top: 0,
            width: containerWidth,
            height: maxBarHeight + BOTTOM_OFFSET,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {versions
            .filter((candidate) => candidate.id !== versionId)
            .map((candidate) => (
              <PreRenderedProposalBarsStage
                key={candidate.id}
                version={candidate}
                containerWidth={containerWidth}
                maxBarHeight={maxBarHeight}
              />
            ))}
        </Box>
      )}
      {targetLineBottom !== null && (
        <Tooltip
          title={targetTooltip}
          followCursor
          disableInteractive
          placement="top"
        >
          <Box
            onClick={handleTargetLineClick}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: targetLineBottom - 4,
              height: 12,
              cursor: 'pointer',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              '&::after': {
                content: '""',
                display: 'block',
                width: '100%',
                borderTop: '1px dashed rgba(0,0,0,0.25)',
              },
            }}
          />
        </Tooltip>
      )}
      {/* Warm-surface plate behind the bar name area, cut off at the target. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: targetLineBottom !== null ? targetLineBottom + 2 : '100%',
          backgroundColor: 'warmSurface.dark',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Box
        key={versionId}
        ref={barsStageRef}
        data-proposal-bars-stage=""
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          width: '100%',
          flexShrink: 0,
          pointerEvents: 'none',
          '& [data-proposal-bar]': {
            pointerEvents: 'auto',
          },
        }}
      >
        {/* Remount numeric displays on version switches so they start at the
            new version's value instead of springing from the previous one. */}
        {showBaseline && (
          <BaselineBar
            key={versionId}
            barWidth={barWidth}
            maxBarHeight={maxBarHeight}
            co2ePerGfaMax={co2ePerGfaMax}
            costPerGfaMax={costPerGfaMax}
            showCo2LabelIcon={showCo2LabelIcon}
            onDeselectGroup={onDeselectGroup}
          />
        )}

        {orderedProposals.map((proposal, index) => {
          const isDropTarget = dropTarget?.id === proposal.id;
          return (
            // Proposal ids can survive a duplicated version. Include the version so
            // KineticNumberDisplay does not interpolate across versions.
            <Box
              key={`${versionId}:${proposal.id}`}
              ref={(el: HTMLDivElement | null) => {
                if (el) barRefsMap.current.set(proposal.id, el);
                else barRefsMap.current.delete(proposal.id);
              }}
              draggable={!readonly}
              onDragStart={(e) => handleDragStart(e, proposal.id)}
              onDragOver={(e) => handleDragOver(e, proposal.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, proposal.id)}
              onDragEnd={handleDragEnd}
              sx={{
                position: 'relative',
                cursor: readonly ? 'default' : 'grab',

                borderLeft:
                  isDropTarget && dropTarget.side === 'left'
                    ? DROP_BORDER
                    : '2px solid transparent',

                borderRight:
                  isDropTarget && dropTarget.side === 'right'
                    ? DROP_BORDER
                    : '2px solid transparent',

                transition: 'border-color 0.1s ease-in-out',
              }}
            >
              <ProposalBar
                proposal={proposal}
                selectedProposalId={displayedSelectedProposalId}
                onSelectProposal={setProposalSelectionImmediately}
                showCo2LabelIcon={showCo2LabelIcon}
                barSwatchColor={getProposalSwatchColor(
                  proposalBarColorIndexById.get(proposal.id) ??
                    proposal.colorIndex,
                )}
                results={
                  proposalsResultRecords[proposal.id]?.[versionId] ??
                  EMPTY_RESULTS
                }
                barWidth={barWidth}
                maxBarHeight={maxBarHeight}
                co2ePerGfaMax={co2ePerGfaMax}
                costPerGfaMax={costPerGfaMax}
                baselineCo2ePerGFA={baselineCo2ePerGFA}
                baselineCostBarHeight={baselineCostBarHeight}
                tooltipPlacement={
                  index * 2 < orderedProposals.length ? 'right' : 'left'
                }
                onDeselectGroup={onDeselectGroup}
              />
            </Box>
          );
        })}
        {!readonly && !versionTransitionActive && (
          <Box
            data-new-proposal-control=""
            sx={{
              position: 'relative',
              alignSelf: 'stretch',
              width: 0,
              flexShrink: 0,
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                bottom: 11,
                left: 8,
                height: 24,
              }}
            >
              <Tooltip title="New proposal" placement="bottom">
                <IconButton
                  category="mui"
                  icon="Add"
                  onClick={handleAddProposal}
                />
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Proposals;
